import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { post } from "../utils/api";
import { toast } from "react-toastify";
import { usePageTitle } from "../hooks/usePageTitle";

type PaymentViewState = "processing" | "pending" | "success" | "error";

interface PaymentConfirmationResponse {
	success: boolean;
	message?: string;
	status?: string | null;
}

const PENDING_PAYMENT_STATUSES = new Set([
	"processing",
	"payment_processing",
	"requires_action",
]);

const PaymentSuccessPage: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [viewState, setViewState] = useState<PaymentViewState>("processing");
	const [message, setMessage] = useState(
		"決済状況を確認しています。このページを閉じずにお待ちください。"
	);
	const [detailMessage, setDetailMessage] = useState<string | null>(null);
	const orderId = searchParams.get("order_id");
	const pendingRetryRef = useRef<number | null>(null);
	const redirectTimerRef = useRef<number | null>(null);
	const toastShownRef = useRef(false);
	const pollCountRef = useRef(0);

	const pageTitle = useMemo(() => {
		switch (viewState) {
			case "success":
				return "購入完了";
			case "pending":
				return "支払い待ち";
			case "error":
				return "決済エラー";
			default:
				return "決済処理中";
		}
	}, [viewState]);

	usePageTitle(pageTitle, { fallback: "購入完了" });

	useEffect(() => {
		let cancelled = false;

		const clearTimers = () => {
			if (pendingRetryRef.current !== null) {
				window.clearTimeout(pendingRetryRef.current);
				pendingRetryRef.current = null;
			}

			if (redirectTimerRef.current !== null) {
				window.clearTimeout(redirectTimerRef.current);
				redirectTimerRef.current = null;
			}
		};

		const handlePendingState = (status: string | null | undefined, pendingMessage?: string) => {
			setViewState("pending");
			setMessage(
				status === "requires_action"
					? "追加の支払い手続きが必要です。購入画面または支払い案内をご確認ください。"
					: "支払いは受け付け済みです。反映までしばらくお待ちください。"
			);
			setDetailMessage(
				pendingMessage ||
					(status === "requires_action"
						? "コンビニ決済や本人認証の完了後にチケットへ反映されます。"
						: "Stripe 側で処理が完了すると、自動でチケットへ反映されます。")
			);

			if (status !== "requires_action" && pollCountRef.current < 6) {
				pollCountRef.current += 1;
				pendingRetryRef.current = window.setTimeout(() => {
					void confirmPayment(true);
				}, 5000);
			}
		};

		const handleErrorState = (errorMessage?: string) => {
			setViewState("error");
			setMessage("決済確認中に問題が発生しました。");
			setDetailMessage(
				errorMessage ||
					"時間をおいて購入履歴をご確認ください。改善しない場合はお問い合わせください。"
			);
			if (!toastShownRef.current) {
				toast.error(errorMessage || "決済確認中にエラーが発生しました。");
				toastShownRef.current = true;
			}
		};

		const handleSuccessState = (successMessage?: string, silent = false) => {
			setViewState("success");
			setMessage("チケットの購入が完了しました。");
			setDetailMessage(
				successMessage || "マイページに自動的に移動します。"
			);
			if (!silent && !toastShownRef.current) {
				toast.success("決済が完了しました！");
				toastShownRef.current = true;
			}
			redirectTimerRef.current = window.setTimeout(() => {
				navigate("/mypage");
			}, 3000);
		};

		const confirmPayment = async (isRetry = false) => {
			clearTimers();

			if (!orderId) {
				setViewState("error");
				setMessage("注文情報が見つかりません。");
				setDetailMessage("イベントページから再度お試しください。");
				return;
			}

			try {
				const response = await post<PaymentConfirmationResponse>(
					"/payments/stripe/payment-success",
					{
						order_id: parseInt(orderId, 10),
					}
				);

				if (cancelled) {
					return;
				}

				if (response.success) {
					handleSuccessState(response.message, isRetry);
					return;
				}

				if (PENDING_PAYMENT_STATUSES.has(response.status ?? "")) {
					handlePendingState(response.status, response.message);
					return;
				}

				handleErrorState(response.message);
			} catch (error) {
				if (cancelled) {
					return;
				}

				const apiError = error as {
					response?: {
						data?: PaymentConfirmationResponse;
					};
				};
				const errorStatus = apiError.response?.data?.status ?? null;
				const errorMessage = apiError.response?.data?.message;

				if (PENDING_PAYMENT_STATUSES.has(errorStatus ?? "")) {
					handlePendingState(errorStatus, errorMessage);
					return;
				}

				handleErrorState(errorMessage);
			}
		};

		void confirmPayment();

		return () => {
			cancelled = true;
			clearTimers();
		};
	}, [orderId, navigate]);

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900">
			<div className="container mx-auto max-w-2xl px-4 py-10">
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
					<div className="flex flex-col items-center">
						{viewState === "processing" && (
							<>
								<div className="w-16 h-16 border-4 border-blue-400 dark:border-blue-500 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin mb-4"></div>
								<h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
									決済処理中...
								</h2>
							</>
						)}

						{viewState === "success" && (
							<>
								<div className="w-16 h-16 flex items-center justify-center bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
									<svg
										className="w-10 h-10 text-green-600 dark:text-green-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
								</div>
								<h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
									決済完了
								</h2>
							</>
						)}

						{viewState === "pending" && (
							<>
								<div className="w-16 h-16 flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 rounded-full mb-4">
									<svg
										className="w-9 h-9 text-amber-600 dark:text-amber-300"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
									支払い待ち
								</h2>
							</>
						)}

						{viewState === "error" && (
							<>
								<div className="w-16 h-16 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
									<svg
										className="w-9 h-9 text-red-600 dark:text-red-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</div>
								<h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
									決済確認エラー
								</h2>
							</>
						)}

						<p className="text-gray-700 dark:text-gray-300">{message}</p>
						{detailMessage && (
							<p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
								{detailMessage}
							</p>
						)}

						<div className="mt-6 flex flex-wrap justify-center gap-3">
							{viewState !== "processing" && (
								<button
									onClick={() => navigate("/mypage")}
									className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-medium transition-all duration-200"
								>
									マイページへ
								</button>
							)}

							{viewState === "pending" && (
								<button
									onClick={() => navigate("/mypage/history")}
									className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
								>
									購入履歴を見る
								</button>
							)}

							{viewState === "error" && (
								<button
									onClick={() => navigate(-1)}
									className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
								>
									前の画面に戻る
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaymentSuccessPage;
